import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatBar from '../components/ChatBar';
import {
  ABOUT_PREVIEW,
  FAQ,
  FEATURES,
  GETTING_STARTED,
  HERO,
  STATS,
  STEPS,
  SUPPORT_TOPICS,
  SYSTEM_REQUIREMENTS,
  TESTIMONIALS,
  WHY_US,
} from '../constants/content';
import {
  SITE_DOMAIN,
  SITE_NAME,
  SITE_TAGLINE,
  SUPPORT_EMAIL,
} from '../constants/site';
import { openZohoChat } from '../utils/openZohoChat';
import { submitLead } from '../utils/submitLead';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const phone = String(data.get('phone') || '').trim();
    const message = String(data.get('message') || '').trim();

    if (!phone) {
      setError('Please enter your phone number.');
      return;
    }

    setPending(true);

    try {
      await submitLead({
        name,
        email,
        phone,
        message,
        source: 'hero-form',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  const currentTestimonial = TESTIMONIALS[testimonialIndex];

  return (
    <div className="page">
      <Header />

      <section className="hero">
        <div className="shell hero__grid">
          <div className="hero__copy">
            <p className="hero__eyebrow">{SITE_TAGLINE}</p>
            <h1>{HERO.headline}</h1>
            <p className="hero__text">{HERO.text}</p>
            <ul className="hero__list">
              {HERO.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="hero__actions">
              <button type="button" className="btn btn--primary" onClick={openZohoChat}>
                Chat with Support
              </button>
            </div>
            <p className="hero__domain">{SITE_DOMAIN}</p>
          </div>

          <aside className="hero__form-wrap" aria-label="Send us a query">
            {sent ? (
              <div className="form-card form-card--done">
                <h2>Thank you</h2>
                <p>
                  We received your message and will reply soon. You can also reach us at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => {
                    setSent(false);
                    setError('');
                  }}
                >
                  Send another query
                </button>
              </div>
            ) : (
              <form className="form-card" onSubmit={handleSubmit} noValidate>
                <h2>Send us a Query</h2>
                <p className="form-card__sub">Tell us what you need help with.</p>

                <input
                  type="text"
                  name="_honey"
                  className="form-card__honey"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <label htmlFor="name">
                  Full name
                  <input id="name" name="name" type="text" autoComplete="name" required placeholder="Your name" />
                </label>

                <label htmlFor="email">
                  Email
                  <input id="email" name="email" type="email" autoComplete="email" required placeholder="you@email.com" />
                </label>

                <label htmlFor="phone">
                  Phone
                  <input id="phone" name="phone" type="tel" autoComplete="tel" required placeholder="+1 555 000 0000" />
                </label>

                <label htmlFor="message">
                  How can we help?
                  <textarea id="message" name="message" rows={3} required placeholder="Install, sync, TreeVault…" />
                </label>

                {error ? (
                  <p className="form-card__error" role="alert">
                    {error}
                  </p>
                ) : null}

                <button type="submit" className="btn btn--primary btn--block" disabled={pending}>
                  {pending ? 'Sending…' : 'Submit Query'}
                </button>
              </form>
            )}
          </aside>
        </div>
      </section>

      <section className="stats" aria-label="Support highlights">
        <div className="shell stats__grid">
          {STATS.map((stat) => (
            <div key={stat.label} className="stats__item">
              <p className="stats__value">{stat.value}</p>
              <p className="stats__label">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="about" className="section">
        <div className="shell split">
          <div className="section-head">
            <p className="section-kicker">About us</p>
            <h2>{ABOUT_PREVIEW.title}</h2>
            <p>{ABOUT_PREVIEW.text}</p>
            <ul className="check-list">
              {ABOUT_PREVIEW.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <Link to="/about" className="btn btn--ghost">
              Learn more about us
            </Link>
          </div>
          <div className="about-panel">
            <p className="about-panel__label">Help desk at a glance</p>
            <div className="about-panel__grid">
              <div className="about-panel__cell">
                <strong>Install</strong>
                <span>First-time setup & activation</span>
              </div>
              <div className="about-panel__cell">
                <strong>Sync</strong>
                <span>Multi-device & TreeVault</span>
              </div>
              <div className="about-panel__cell">
                <strong>Migrate</strong>
                <span>New PC or Mac transfers</span>
              </div>
              <div className="about-panel__cell">
                <strong>Fix</strong>
                <span>Errors, media & FamilySearch</span>
              </div>
            </div>
            <button type="button" className="btn btn--primary btn--block" onClick={openZohoChat}>
              Talk to an Expert
            </button>
          </div>
        </div>
      </section>

      <section id="features" className="section section--wash">
        <div className="shell">
          <div className="section-head">
            <p className="section-kicker">What we help with</p>
            <h2>Simple support for everyday Family Tree Maker tasks</h2>
          </div>
          <div className="cards">
            {FEATURES.map((item) => (
              <article key={item.title} className="card">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="why-us" className="section">
        <div className="shell">
          <div className="section-head section-head--center">
            <p className="section-kicker">Why choose us</p>
            <h2>Support built for genealogists, not call scripts</h2>
          </div>
          <div className="cards cards--2">
            {WHY_US.map((item) => (
              <article key={item.title} className="card card--icon">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="section section--wash">
        <div className="shell">
          <div className="section-head">
            <p className="section-kicker">How it works</p>
            <h2>Get help in three easy steps</h2>
          </div>
          <ol className="steps">
            {STEPS.map((item) => (
              <li key={item.step} className="step">
                <span className="step__num">{item.step}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section--cta">
        <div className="shell cta-band">
          <div>
            <p className="section-kicker">Need a hand?</p>
            <h2>Stuck during setup or sync?</h2>
            <p>Open live chat — our team guides you through install, upgrade, and migration on Windows or Mac.</p>
          </div>
          <button type="button" className="btn btn--light" onClick={openZohoChat}>
            Open Live Chat
          </button>
        </div>
      </section>

      <section id="getting-started" className="section">
        <div className="shell split">
          <div className="section-head">
            <p className="section-kicker">Before you begin</p>
            <h2>Smart habits for a smoother setup</h2>
            <p>
              A little preparation goes a long way — especially when you are working with our support team on an
              install, upgrade, or migration.
            </p>
            <button type="button" className="btn btn--primary" onClick={openZohoChat}>
              Talk to an Expert
            </button>
          </div>
          <div className="tip-grid">
            {GETTING_STARTED.map((tip) => (
              <article key={tip.title} className="tip-card">
                <h3>{tip.title}</h3>
                <p>{tip.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="support" className="section section--wash">
        <div className="shell">
          <div className="section-head">
            <p className="section-kicker">Common issues</p>
            <h2>Topics we handle every day</h2>
          </div>
          <ul className="topics">
            {SUPPORT_TOPICS.map((topic) => (
              <li key={topic}>{topic}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="requirements" className="section section--dark">
        <div className="shell">
          <div className="section-head section-head--center section-head--light">
            <p className="section-kicker">Compatibility</p>
            <h2>System requirements</h2>
            <p>Confirm your Windows or Mac system meets these minimum specs before you install.</p>
          </div>
          <div className="req-grid">
            {(['windows', 'mac']).map((platform) => (
              <div key={platform} className="req-col">
                <h3>{platform === 'windows' ? 'Windows' : 'macOS'}</h3>
                <dl>
                  {SYSTEM_REQUIREMENTS[platform].map((item) => (
                    <div key={item.label} className="req-row">
                      <dt>{item.label}</dt>
                      <dd>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="section">
        <div className="shell split split--testimonial">
          <div className="section-head">
            <p className="section-kicker">Experiences</p>
            <h2>What users tell us</h2>
            <p>Feedback from people who got their Family Tree Maker projects back on track.</p>
            <div className="testimonial-nav">
              <button
                type="button"
                aria-label="Previous testimonial"
                onClick={() =>
                  setTestimonialIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)
                }
              >
                ←
              </button>
              <button
                type="button"
                aria-label="Next testimonial"
                onClick={() => setTestimonialIndex((i) => (i + 1) % TESTIMONIALS.length)}
              >
                →
              </button>
            </div>
          </div>
          <blockquote className="testimonial">
            <p>&ldquo;{currentTestimonial.quote}&rdquo;</p>
            <footer>
              <cite>
                {currentTestimonial.name}
                <span> — {currentTestimonial.location}</span>
              </cite>
              <div className="testimonial-dots">
                {TESTIMONIALS.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={`Go to testimonial ${index + 1}`}
                    className={index === testimonialIndex ? 'is-active' : ''}
                    onClick={() => setTestimonialIndex(index)}
                  />
                ))}
              </div>
            </footer>
          </blockquote>
        </div>
      </section>

      <section id="faq" className="section section--wash">
        <div className="shell">
          <div className="section-head">
            <p className="section-kicker">FAQ</p>
            <h2>Frequently asked questions</h2>
          </div>
          <div className="faq">
            {FAQ.map((item) => (
              <details key={item.q} className="faq__item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--cta section--cta-dark">
        <div className="shell cta-band">
          <div>
            <h2>Ready to get started?</h2>
            <p>Open live chat — {SITE_NAME} support is available 24/7.</p>
          </div>
          <button type="button" className="btn btn--light" onClick={openZohoChat}>
            Chat Now
          </button>
        </div>
      </section>

      <Footer />
      <ChatBar />
    </div>
  );
}
